/**
 * Soil data library: library-safe shaping and filtering of published,
 * staff-confirmed lab reports. Values are compared through measurementSummary
 * so verdicts match the rest of the app. Files, raw data, notes and the exact
 * address are never part of a LibraryReport.
 */
import { compareRow, fallbackRows, type SummaryInputRow, type SummaryRow } from "@/lib/measurementSummary";
import type { MeasurementSource } from "@/lib/evidenceParser";
import { milesBetween, zipCentroid } from "@/data/zipCentroids";
import type { MaterialType, QualityTier } from "@/types";

export interface LibraryReport {
  id: string;
  title: string;
  labName: string;
  reportDate: string | null;
  materialType: MaterialType | null;
  nyTier: QualityTier | null;
  njTier: QualityTier | null;
  passesNy: boolean;
  passesNj: boolean;
  confirmedAt: string | null;
  listingId: string | null;
  city: string;
  state: string;
  zip: string;
  measurements: SummaryInputRow[];
}

type Raw = Record<string, unknown>;

/** Only these keys survive — anything else the server might send is dropped. */
export function toLibraryReports(rows: unknown[]): LibraryReport[] {
  return (rows as Raw[])
    .filter((r) => r && r.passes_ny !== null && r.passes_ny !== undefined && r.passes_nj !== null && r.passes_nj !== undefined)
    .map((r) => {
      const id = String(r.lab_report_id);
      const title = String(r.title ?? "Lab report");
      const table = Array.isArray(r.measurements) ? (r.measurements as Raw[]) : [];
      const measurements: SummaryInputRow[] = table.length
        ? table.map((m) => ({
            labReportId: id, reportTitle: title, ownerId: "", documentId: null,
            fileName: String(m.file_name ?? "") || "Lab data", uploadedAt: null,
            name: String(m.name ?? ""), cas: String(m.cas ?? ""), value: Number(m.value),
            unit: String(m.unit ?? "") || "mg/kg", nonDetect: m.non_detect === true,
            source: m.source === "ai_extracted_pdf" ? "ai_extracted_pdf" : ("file_parsed" as MeasurementSource),
          }))
        : fallbackRows({ id, title, user_id: "", analysis: { evidence_measurements: r.evidence ?? [] } });
      return {
        id, title,
        labName: String(r.lab_name ?? ""),
        reportDate: (r.report_date as string) ?? null,
        materialType: (r.material_type as MaterialType) ?? null,
        nyTier: (r.ny_quality_tier as QualityTier) ?? null,
        njTier: (r.nj_quality_tier as QualityTier) ?? null,
        passesNy: r.passes_ny === true,
        passesNj: r.passes_nj === true,
        confirmedAt: (r.confirmed_at as string) ?? null,
        listingId: (r.listing_id as string) ?? null,
        city: String(r.city ?? ""),
        state: String(r.state ?? ""),
        zip: String(r.zip ?? ""),
        measurements,
      };
    });
}

export interface LibraryFilters {
  parameter?: string;
  maxValue?: number | null;
  tier?: QualityTier | "all";
  passes?: "any" | "ny" | "nj" | "both";
  state?: "all" | "NY" | "NJ";
  zip?: string;
  radiusMiles?: number | null;
  material?: MaterialType | "all";
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

export function matchesParameter(r: LibraryReport, parameter: string, maxValue: number | null | undefined): boolean {
  const p = norm(parameter);
  const hits = r.measurements.filter((m) => norm(m.name).includes(p) || (m.cas && m.cas === parameter.trim()));
  if (hits.length === 0) return false;
  if (maxValue == null) return true;
  // Every reading of that parameter must be at or below the ceiling; non-detects qualify.
  return hits.every((m) => m.nonDetect || m.value <= maxValue);
}

export function filterLibrary(list: LibraryReport[], f: LibraryFilters): LibraryReport[] {
  const origin = f.zip && f.radiusMiles ? zipCentroid(f.zip) : null;
  return list.filter((r) => {
    if (f.parameter?.trim() && !matchesParameter(r, f.parameter, f.maxValue)) return false;
    if (f.tier && f.tier !== "all" && r.nyTier !== f.tier && r.njTier !== f.tier) return false;
    if (f.passes === "ny" && !r.passesNy) return false;
    if (f.passes === "nj" && !r.passesNj) return false;
    if (f.passes === "both" && !(r.passesNy && r.passesNj)) return false;
    if (f.state && f.state !== "all" && r.state !== f.state) return false;
    if (f.material && f.material !== "all" && r.materialType !== f.material) return false;
    if (f.zip?.trim()) {
      if (origin) {
        const here = zipCentroid(r.zip);
        if (!here || milesBetween(origin, here) > (f.radiusMiles ?? 0)) return false;
      } else if (!r.zip.startsWith(f.zip.trim())) return false;
    }
    return true;
  });
}

export function compareLibraryRows(r: LibraryReport, tier: QualityTier): SummaryRow[] {
  return r.measurements.map((m) => compareRow(m, tier, r.materialType ?? "general-fill"));
}
